import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone,
  MessageCircle,
  Mail,
  Globe,
  MapPin,
  Send,
  CheckCircle2,
  ArrowUpRight,
  ShieldCheck,
  Lock,
  GraduationCap,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Logo } from "./Sections";
import { LEGAL_LINKS } from "../legal/links";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES_LIST = [
  "EUSS — Administrative Support",
  "Universal Credit Administrative Support",
  "PIP / DLA Form Support",
  "ETA Application Administrative Assistance",
  "Council Tax Support",
  "Student Finance England Support",
  "University Application Administration",
  "Smart Tools / Business Support",
  "Payroll Administration",
  "Self Assessment Administrative Support",
  "Bookkeeping",
  "Tax Return Documentation Support",
  "Administrative Case & Dispute Support",
];

const CONTACT_CARDS = [
  { id: "phone", href: "tel:07383513170", icon: Phone, orange: true, label: "Phone", value: "07383513170" },
  { id: "email", href: "mailto:info@mariowayltd.com", icon: Mail, label: "Email", value: "info@mariowayltd.com" },
  { id: "website", href: "https://www.mariowayltd.com", icon: Globe, label: "Website", value: "www.mariowayltd.com", external: true },
  { id: "whatsapp", href: "https://wa.me/447383513170", icon: MessageCircle, orange: true, label: "WhatsApp", value: "07383513170", external: true },
];

const EMPTY_FORM = { name: "", email: "", phone: "", service: "", message: "" };

const ContactCard = ({ id, href, icon: Icon, orange, label, value, external }) => (
  <a
    href={href}
    {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    className="soft-card p-5 flex items-center gap-4 hover:border-[#F37021]"
    data-testid={`contact-${id}`}
  >
    <span className={`icon-tile ${orange ? "orange" : ""}`}><Icon size={20} /></span>
    <div>
      <div className="text-[12px] uppercase tracking-[0.16em] text-[#4A4A4A] font-bold">{label}</div>
      <div className="font-display text-xl text-[#002B7F]">{value}</div>
    </div>
    <ArrowUpRight className="ml-auto text-[#002B7F]" />
  </a>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-[13px] font-bold text-[#002B7F]">{label}</label>
    {children}
  </div>
);

const ContactForm = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks — we'll be in touch shortly.");
      setSuccess(true);
      setForm(EMPTY_FORM);
    } catch {
      toast.error("Something went wrong. Please call 07383513170.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="soft-card p-6 md:p-8" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full name *">
          <input className="input-field mt-2" placeholder="Your name" value={form.name} onChange={update("name")} data-testid="contact-input-name" required />
        </Field>
        <Field label="Email *">
          <input type="email" className="input-field mt-2" placeholder="you@example.com" value={form.email} onChange={update("email")} data-testid="contact-input-email" required />
        </Field>
        <Field label="Phone">
          <input className="input-field mt-2" placeholder="UK mobile (optional)" value={form.phone} onChange={update("phone")} data-testid="contact-input-phone" />
        </Field>
        <Field label="Service">
          <select className="input-field mt-2" value={form.service} onChange={update("service")} data-testid="contact-input-service">
            <option value="">Select a service</option>
            {SERVICES_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      </div>
      <div className="mt-4">
        <Field label="Your message *">
          <textarea className="input-field mt-2 min-h-[140px]" placeholder="Tell us briefly how we can help…" value={form.message} onChange={update("message")} data-testid="contact-input-message" required />
        </Field>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[13px] text-[#4A4A4A]">
          <Lock size={14} className="text-[#F37021]" /> Confidential — by sending you agree to our <Link to="/privacy-policy" className="underline" data-testid="contact-form-privacy-link">Privacy Policy</Link>
        </div>
        <button type="submit" disabled={submitting} className="btn-orange disabled:opacity-70 disabled:cursor-not-allowed" data-testid="contact-submit">
          {submitting ? "Sending…" : (<><Send size={16} /> Send message</>)}
        </button>
      </div>

      {success && (
        <div className="mt-6 flex items-center gap-2 text-[#002B7F] bg-[#F5F7FA] border border-[#E6EAF0] rounded-xl p-4" data-testid="contact-success">
          <CheckCircle2 className="text-[#F37021]" /> Thanks — your enquiry has been received.
        </div>
      )}
    </form>
  );
};

export const Contact = () => (
  <section id="contact" className="section bg-[#F5F7FA]">
    <div className="container-x grid md:grid-cols-12 gap-10">
      <div className="md:col-span-5">
        <span className="eyebrow"><MessageCircle size={12} /> Get in touch</span>
        <h2 className="font-display text-4xl sm:text-5xl mt-4 text-[#002B7F] leading-[1.05]">
          Call or WhatsApp for a <span className="text-[#F37021]">free initial 15-minute consultation.</span>
        </h2>
        <p className="mt-4 text-[#4A4A4A]">
          Prefer to speak directly? Reach us on the number below — or drop a message and we'll come back to you as soon as possible, normally within one working day.
        </p>
        <p className="mt-3 text-[12.5px] text-[#6B7280] leading-relaxed" data-testid="contact-consultation-note">
          The initial consultation is intended to understand the nature of your enquiry and does not itself constitute regulated legal, immigration, tax or financial advice.
        </p>

        <div className="mt-8 space-y-4">
          {CONTACT_CARDS.map((c) => <ContactCard key={c.id} {...c} />)}
          <a href="https://wa.me/447383513170" target="_blank" rel="noopener noreferrer" className="btn-orange w-full justify-center" data-testid="contact-whatsapp-btn">
            <MessageCircle size={18} /> WhatsApp us now
          </a>
        </div>

        <div className="mt-6 flex items-center gap-3 text-[#4A4A4A] text-sm">
          <ShieldCheck size={14} className="text-[#F37021]" /> Your details are treated confidentially — see our <Link to="/privacy-policy" className="underline text-[#002B7F]" data-testid="contact-privacy-link">Privacy Policy</Link>.
        </div>
      </div>

      <div className="md:col-span-7">
        <ContactForm />
      </div>
    </div>
  </section>
);

export const Footer = () => (
  <footer className="navy-gradient text-white relative overflow-hidden">
    <div className="hero-orb" style={{ width: 360, height: 360, background: "#F37021", top: -160, left: -120, opacity: 0.25 }} />
    <div className="container-x relative py-16">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <Logo variant="light" />
          <p className="mt-5 text-white/75 max-w-sm">
            Marioway Benefits Consultancy LTD — Independent administrative and consultancy services.
            Not affiliated with or endorsed by any UK government department or public authority.
          </p>
          <p className="mt-3 text-[13px] text-white/70 leading-relaxed" data-testid="footer-company-details">
            Company Number: 12784422<br />Registered in England and Wales<br />Leeds, United Kingdom
          </p>
          <p className="mt-3 text-[#FF8C42] font-semibold">Amway | IBO</p>
          <div className="mt-5 flex gap-3">
            <a href="tel:07383513170" className="btn-orange" data-testid="footer-call">
              <Phone size={16} /> 07383513170
            </a>
            <a href="https://wa.me/447383513170" target="_blank" rel="noopener noreferrer" className="btn-navy" data-testid="footer-whatsapp">
              <MessageCircle size={16} /> WhatsApp
            </a>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="text-[12px] uppercase tracking-[0.18em] text-[#FF8C42] font-bold mb-4">Services</div>
          <ul className="space-y-2 text-white/80 text-[14px]">
            {SERVICES_LIST.slice(0, 6).map((s) => (
              <li key={s}><a href="/#services" className="hover:text-[#FF8C42] transition-colors">{s}</a></li>
            ))}
          </ul>
          <div className="text-[12px] uppercase tracking-[0.18em] text-[#FF8C42] font-bold mb-4 mt-8">Legal</div>
          <ul className="space-y-2 text-white/80 text-[14px]">
            {LEGAL_LINKS.map((l) => (
              <li key={l.to}><Link to={l.to} className="hover:text-[#FF8C42] transition-colors" data-testid={`footer-link-${l.to.slice(1)}`}>{l.label}</Link></li>
            ))}
            <li>
              <button type="button" onClick={() => window.dispatchEvent(new Event("mw:cookie-settings"))} className="hover:text-[#FF8C42] transition-colors" data-testid="footer-cookie-settings">
                Cookie settings
              </button>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="text-[12px] uppercase tracking-[0.18em] text-[#FF8C42] font-bold mb-4">Contact</div>
          <ul className="space-y-3 text-white/85 text-[14px]">
            <li className="flex items-center gap-2"><Phone size={14} /> 07383513170</li>
            <li className="flex items-center gap-2"><MessageCircle size={14} /> WhatsApp 07383513170</li>
            <li className="flex items-center gap-2"><Mail size={14} /> info@mariowayltd.com</li>
            <li className="flex items-center gap-2"><Globe size={14} /> www.mariowayltd.com</li>
            <li className="flex items-center gap-2"><MapPin size={14} /> Leeds, United Kingdom</li>
          </ul>
          <div className="mt-6 text-[13px] text-white/70 flex items-center gap-2">
            <Lock size={13} className="text-[#FF8C42]" /> Secure Payments. Trusted Service.
          </div>
        </div>
      </div>

      <div className="mt-10 pt-8 border-t border-white/10" data-testid="footer-qualifications">
        <div className="grid md:grid-cols-12 gap-6 items-start">
          <div className="md:col-span-3">
            <div className="text-[12px] uppercase tracking-[0.18em] text-[#FF8C42] font-bold flex items-center gap-2">
              <GraduationCap size={13} /> Founder &amp; Director
            </div>
          </div>
          <div className="md:col-span-9">
            <p className="text-[13.5px] text-white/85 font-light leading-relaxed">
              <span className="font-semibold">Florin-Marius Bran</span> — BSc Accounting and Financial Management<br />
              Canterbury Christ Church University · 2026
            </p>
            <p className="mt-3 text-[11.5px] text-white/55 italic font-light max-w-2xl leading-relaxed">
              Academic qualifications are stated for transparency. Professional or regulated services are provided only where Marioway holds the appropriate authorisation or where the activity does not require regulatory authorisation.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-12 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 text-[13px] text-white/70">
        <span>© {new Date().getFullYear()} Marioway Benefits Consultancy LTD · Company Number 12784422 · Registered in England and Wales · Leeds, UK</span>
        <span>Independent administrative and consultancy services. Not affiliated with or endorsed by any UK government department or public authority.</span>
      </div>
    </div>
  </footer>
);
